<template>
  <div class="lang" v-click-outside="close">
    <button
      type="button"
      class="lang__btn"
      :class="{ 'lang__btn--open': open }"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="listbox"
      :aria-label="$t('siteUi.selectedLanguage')"
      @click="open = !open"
      @keydown.down.prevent="openAndFocusFirst"
      @keydown.esc="close"
    >
      <img :src="flagOf(currentLanguage)" class="lang__flag" alt="" />
      <span class="lang__code">{{ shortOf(currentLanguage) }}</span>
      <Icon class="lang__caret" icon="mdi:chevron-down" />
    </button>

    <transition name="lang-pop">
      <ul v-if="open" ref="list" class="lang__menu" role="listbox" @keydown.esc="close">
        <li v-for="lang in availableLanguages" :key="lang.code" role="none">
          <button
            type="button"
            role="option"
            :aria-selected="currentLanguage === lang.code ? 'true' : 'false'"
            :class="['lang__item', { 'lang__item--on': currentLanguage === lang.code }]"
            @click="choose(lang.code)"
          >
            <img :src="flagOf(lang.code)" class="lang__flag" alt="" />
            <span class="lang__name">{{ lang.name }}</span>
            <Icon v-if="currentLanguage === lang.code" class="lang__tick" icon="mdi:check" />
          </button>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
/**
 * Language picker.
 *
 * REWRITTEN BECAUSE THREE THINGS WERE FIGHTING OVER IT. The old version had
 * Bootstrap's `data-bs-toggle="dropdown"`, a hand-made `new Dropdown()` in JS,
 * AND a CSS rule `.language-switch:hover .dropdown-menu { display: block }`.
 * Measured behaviour before the rewrite:
 *
 *   hover                  -> display:block, but WITHOUT bootstrap's .show class
 *   click while hovered    -> still no .show — the click had no visible effect
 *   pointer moves away     -> vanishes instantly, mid-reach
 *
 * So it opened on hover and shut the moment you moved toward the item you
 * wanted, and clicking — the only thing that works on a touch screen — did
 * nothing you could see. Three mechanisms, none of them in charge.
 *
 * Now: ONE mechanism. Click toggles, click-outside closes, Escape closes. No
 * hover-to-open at all — a menu that opens because the pointer crossed it is a
 * menu that opens when you were reaching for something else.
 *
 * The flag now sits beside a language CODE. A bare flag asks the reader to
 * know which country stands for which language, and it is the one control on
 * the page whose whole job is to be understood by someone who cannot read the
 * current language.
 */
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { Icon } from "@iconify/vue";

import swFlag from "@/assets/images/flag-tanzania.jpg";
import enFlag from "@/assets/images/en.svg";

const clickOutside = {
  beforeMount(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("mousedown", el.__clickOutsideHandler__);
  },
  unmounted(el) {
    document.removeEventListener("mousedown", el.__clickOutsideHandler__);
  },
};

export default {
  name: "LanguageSwitch",
  components: { Icon },
  directives: { "click-outside": clickOutside },

  setup() {
    const store = useStore();
    const { locale } = useI18n();
    const open = ref(false);
    const list = ref(null);

    const availableLanguages = [
      { code: "sw", name: "Kiswahili", short: "SW" },
      { code: "en", name: "English", short: "EN" },
    ];

    const currentLanguage = computed(() => store.state.currentLanguage || locale.value || "sw");

    const flagOf = (code) => (code === "en" ? enFlag : swFlag);
    const shortOf = (code) => (availableLanguages.find((l) => l.code === code) || {}).short || "SW";

    const close = () => { open.value = false; };

    /** Keyboard users land on the first option rather than nowhere. */
    const openAndFocusFirst = async () => {
      open.value = true;
      await nextTick();
      const first = list.value && list.value.querySelector(".lang__item");
      if (first) first.focus();
    };

    const choose = (code) => {
      if (code !== currentLanguage.value) {
        store.dispatch("changeLanguage", code);
        locale.value = code;
      }
      close();
    };

    return {
      open, list, availableLanguages, currentLanguage,
      flagOf, shortOf, close, choose, openAndFocusFirst,
    };
  },
};
</script>

<style scoped>
.lang { position: relative; display: inline-flex; }

/* ── Trigger ─────────────────────────────────────────────── */
.lang__btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 10px;
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: var(--radius-pill, 999px);
  background: var(--color-surface, #fff);
  color: var(--color-text-2, #475569);
  font: 700 12px/1 var(--font-sans, sans-serif);
  letter-spacing: 0.04em;
  cursor: pointer;
  /* Colour only — nothing that moves. A control that shifts under the pointer
     is the thing that made the old one feel unreliable. */
  transition: background-color .15s ease, border-color .15s ease;
}
.lang__btn:hover { background: var(--color-bg, #f8fafc); border-color: var(--color-muted, #94a3b8); }
.lang__btn--open { background: var(--color-bg, #f1f5f9); border-color: var(--color-muted, #94a3b8); }
.lang__btn:focus-visible {
  outline: 2px solid var(--color-primary, #1158ab);
  outline-offset: 2px;
}

.lang__flag {
  width: 20px; height: 14px;
  object-fit: cover;
  border-radius: 2px;
  flex: 0 0 auto;
}
.lang__code { font-variant-numeric: tabular-nums; }
.lang__caret {
  font-size: 15px;
  color: var(--color-muted, #94a3b8);
  transition: transform .15s ease;
}
.lang__btn--open .lang__caret { transform: rotate(180deg); }

/* ── Menu ────────────────────────────────────────────────── */
.lang__menu {
  position: absolute; top: calc(100% + 8px); right: 0; z-index: 1100;
  min-width: 168px;
  margin: 0; padding: 6px;
  list-style: none;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: var(--radius-md, 12px);
  box-shadow: var(--shadow-lg, 0 16px 40px rgba(8, 12, 28, 0.16));
}

.lang__item {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: var(--radius-sm, 8px);
  background: transparent;
  color: var(--color-text, #0b1224);
  font: 600 13.5px/1 var(--font-sans, sans-serif);
  text-align: left;
  cursor: pointer;
}
.lang__item:hover { background: var(--color-bg, #f1f5f9); }
.lang__item:focus-visible {
  outline: 2px solid var(--color-primary, #1158ab);
  outline-offset: -2px;
}
/* The current language is marked by a tick as well as weight — colour alone is
   easy to miss, and this menu has only two entries to compare. */
.lang__item--on { color: var(--color-primary, #1158ab); }
.lang__name { flex: 1 1 auto; }
.lang__tick { font-size: 16px; flex: 0 0 auto; }

/* ── Motion ──────────────────────────────────────────────── */
.lang-pop-enter-active, .lang-pop-leave-active {
  transition: opacity .12s ease, transform .12s ease;
}
.lang-pop-enter-from, .lang-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .lang__btn, .lang__caret,
  .lang-pop-enter-active, .lang-pop-leave-active { transition: none; }
  .lang-pop-enter-from, .lang-pop-leave-to { opacity: 1; transform: none; }
}
</style>
